import { useRef, useEffect, useState, useCallback } from 'react'
import { getLiveGraph, getGraphNodeSources } from '../services/api'

// Three visually distinct colors — orange, cyan, violet
const STATUS_COLORS = {
  hot:  [249, 115,  22],  // orange  — brand new / just extracted
  warm: [  6, 182, 212],  // cyan    — growing / referenced again
  cold: [139,  92, 246],  // violet  — settled / older in session
}

const RADIUS = { hot: 7, warm: 5, cold: 4 }

export default function LiveCallGraph({ huddleId }) {
  const canvasRef    = useRef(null)
  const nodesRef     = useRef([])
  const animRef      = useRef(null)
  const [count, setCount]       = useState(0)
  const [status, setStatus]     = useState('listening')
  const [selected, setSelected] = useState(null)   // { id, title, x, y }
  const [detail, setDetail]     = useState(null)    // fetched node detail
  const [loading, setLoading]   = useState(false)

  // ── Data polling ─────────────────────────────────────────────────────────
  const fetchGraph = useCallback(async () => {
    if (!huddleId) return
    try {
      const data = await getLiveGraph(huddleId)
      if (!data?.concepts) return

      setCount(data.concepts.length)
      setStatus(data.concepts.length > 0 ? 'live' : 'listening')

      const canvas = canvasRef.current
      const W = canvas ? canvas.offsetWidth  : 340
      const H = canvas ? canvas.offsetHeight : 400

      const existing = new Map(nodesRef.current.map(n => [n.id, n]))

      nodesRef.current = data.concepts.map(c => {
        if (existing.has(c.id)) {
          const n = existing.get(c.id)
          n.status  = c.status || n.status
          n.title   = c.title  || n.title
          n.summary = c.summary || n.summary
          return n
        }
        const angle = Math.random() * Math.PI * 2
        const dist  = 30 + Math.random() * 50
        return {
          id:      c.id,
          title:   c.title,
          status:  c.status  || 'warm',
          domain:  c.domain  || '',
          summary: c.summary || '',
          x:  W / 2 + Math.cos(angle) * dist,
          y:  H / 2 + Math.sin(angle) * dist,
          vx: (Math.random() - 0.5) * 0.6,
          vy: (Math.random() - 0.5) * 0.6,
          scale: 0,
        }
      })
    } catch {
      setStatus('error')
    }
  }, [huddleId])

  useEffect(() => {
    fetchGraph()
    const id = setInterval(fetchGraph, 5000)
    return () => clearInterval(id)
  }, [fetchGraph])

  // ── Click handler — hit-test nodes ───────────────────────────────────────
  const handleCanvasClick = useCallback(async (e) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const cx = e.clientX - rect.left
    const cy = e.clientY - rect.top

    // Find the closest node within click radius
    let hit = null
    let minDist = Infinity
    for (const n of nodesRef.current) {
      if (n.scale < 0.4) continue
      const baseR = RADIUS[n.status] || 5
      const hitR  = baseR * n.scale + 10  // generous hit area
      const dx = cx - n.x, dy = cy - n.y
      const d = Math.sqrt(dx * dx + dy * dy)
      if (d < hitR && d < minDist) {
        minDist = d
        hit = n
      }
    }

    if (!hit) {
      setSelected(null)
      setDetail(null)
      return
    }

    // Same node clicked again — toggle off
    if (selected?.id === hit.id) {
      setSelected(null)
      setDetail(null)
      return
    }

    setSelected({ id: hit.id, title: hit.title, x: hit.x, y: hit.y })
    setDetail(null)
    setLoading(true)

    try {
      const data = await getGraphNodeSources(huddleId, hit.id)
      setDetail(data)
    } catch {
      setDetail({ error: true })
    } finally {
      setLoading(false)
    }
  }, [huddleId, selected])

  // ── Canvas animation ──────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let W = 0, H = 0

    function setupCanvas() {
      const pr = window.devicePixelRatio || 1
      W = canvas.offsetWidth
      H = canvas.offsetHeight
      canvas.width  = W * pr
      canvas.height = H * pr
      ctx.setTransform(pr, 0, 0, pr, 0, 0)
    }
    setupCanvas()

    function animate() {
      ctx.clearRect(0, 0, W, H)
      const nodes = nodesRef.current

      // ── Physics ────────────────────────────────────────────────────────
      for (const n of nodes) {
        if (n.scale < 1) n.scale = Math.min(1, n.scale + 0.035)

        for (const m of nodes) {
          if (n === m) continue
          const dx = n.x - m.x
          const dy = n.y - m.y
          const d  = Math.sqrt(dx * dx + dy * dy) || 1
          if (d < 130) {
            const f = (130 - d) / 130 * 0.45
            n.vx += (dx / d) * f
            n.vy += (dy / d) * f
          }
        }

        n.vx += (W / 2 - n.x) * 0.0025
        n.vy += (H / 2 - n.y) * 0.0025
        n.vx *= 0.86
        n.vy *= 0.86
        n.x  += n.vx
        n.y  += n.vy
        n.x = Math.max(64, Math.min(W - 64, n.x))
        n.y = Math.max(32, Math.min(H - 32, n.y))
      }

      // ── Edges ─────────────────────────────────────────────────────────
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j]
          const dx = a.x - b.x, dy = a.y - b.y
          const d  = Math.sqrt(dx * dx + dy * dy)
          if (d < 190 && a.scale > 0.25 && b.scale > 0.25) {
            const alpha = (1 - d / 190) * 0.18 * Math.min(a.scale, b.scale)
            ctx.strokeStyle = `rgba(160, 160, 200, ${alpha})`
            ctx.lineWidth   = 0.6
            ctx.beginPath()
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(b.x, b.y)
            ctx.stroke()
          }
        }
      }

      // ── Nodes ─────────────────────────────────────────────────────────
      for (const n of nodes) {
        if (n.scale < 0.02) continue
        const [r, g, b] = STATUS_COLORS[n.status] || STATUS_COLORS.warm
        const baseR = RADIUS[n.status] || 5
        const sz    = baseR * n.scale
        const isSel = selected?.id === n.id

        // Selection ring
        if (isSel) {
          ctx.strokeStyle = `rgba(255, 255, 255, 0.6)`
          ctx.lineWidth   = 1.5
          ctx.beginPath()
          ctx.arc(n.x, n.y, sz + 4, 0, Math.PI * 2)
          ctx.stroke()
        }

        // Halo on hot nodes
        if (n.status === 'hot' && n.scale > 0.4) {
          const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, sz * 3.5)
          grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${0.28 * n.scale})`)
          grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`)
          ctx.fillStyle = grad
          ctx.beginPath()
          ctx.arc(n.x, n.y, sz * 3.5, 0, Math.PI * 2)
          ctx.fill()
        }

        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${(isSel ? 1 : 0.88) * n.scale})`
        ctx.beginPath()
        ctx.arc(n.x, n.y, sz, 0, Math.PI * 2)
        ctx.fill()

        // Label
        if (n.scale > 0.55) {
          const la = (n.scale - 0.55) / 0.45
          ctx.fillStyle    = `rgba(205, 228, 222, ${(isSel ? 1 : 0.82) * la})`
          ctx.font         = `${n.status === 'hot' ? 11 : 10}px -apple-system, BlinkMacSystemFont, sans-serif`
          ctx.textAlign    = 'center'
          ctx.textBaseline = 'top'
          const label = n.title.length > 22 ? n.title.slice(0, 20) + '…' : n.title
          ctx.fillText(label, n.x, n.y + sz + 5)
        }
      }

      // Empty state hint
      if (nodes.length === 0) {
        ctx.fillStyle    = 'rgba(255, 255, 255, 0.12)'
        ctx.font         = '12px -apple-system, sans-serif'
        ctx.textAlign    = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('Concepts will appear as you speak…', W / 2, H / 2)
      }

      animRef.current = requestAnimationFrame(animate)
    }

    animate()

    const ro = new ResizeObserver(() => setupCanvas())
    ro.observe(canvas)

    return () => {
      cancelAnimationFrame(animRef.current)
      ro.disconnect()
    }
  }, [selected])

  // ── Render ────────────────────────────────────────────────────────────────
  const statusDot = status === 'live'
    ? '#06b6d4'
    : status === 'error'
    ? '#ef4444'
    : 'rgba(255,255,255,0.2)'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', position: 'relative' }}>
      {/* Header */}
      <div style={{
        padding: '10px 14px',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        flexShrink: 0,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusDot, flexShrink: 0 }} />
        <span style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(40,200,160,0.9)', fontWeight: 'var(--ofself-weight-semibold)', textTransform: 'uppercase' }}>
          Live Graph
        </span>
        <span style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(255,255,255,0.3)', marginLeft: 'auto' }}>
          {count > 0 ? `${count} concept${count !== 1 ? 's' : ''}` : 'Listening…'}
        </span>
      </div>

      {/* Canvas */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          onClick={handleCanvasClick}
          style={{ width: '100%', height: '100%', display: 'block', cursor: 'pointer' }}
        />

        {/* Node detail panel — slides in from bottom */}
        {selected && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'rgba(12, 18, 28, 0.97)',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            maxHeight: '65%',
            overflowY: 'auto',
            padding: '12px 14px',
            animation: 'slideUp 0.18s ease-out',
          }}>
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--ofself-text-base)', fontWeight: 'var(--ofself-weight-semibold)', color: 'rgba(40,200,160,1)', marginBottom: 2 }}>
                  {selected.title}
                </div>
                {detail?.domain && (
                  <div style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', }}>
                    {detail.domain}
                  </div>
                )}
              </div>
              <button
                onClick={() => { setSelected(null); setDetail(null) }}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: 'var(--ofself-text-lg)', lineHeight: 1, padding: 0 }}
              >×</button>
            </div>

            {loading && (
              <div style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '12px 0' }}>
                Loading…
              </div>
            )}

            {detail && !loading && !detail.error && (
              <>
                {/* Summary */}
                {detail.summary && (
                  <div style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, marginBottom: 10 }}>
                    {detail.summary}
                  </div>
                )}

                {/* Key claims */}
                {detail.key_claims?.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: 5 }}>
                      Key Claims
                    </div>
                    {detail.key_claims.map((claim, i) => (
                      <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                        <span style={{ color: 'rgba(40,200,160,0.6)', flexShrink: 0, fontSize: 'var(--ofself-text-sm)' }}>·</span>
                        <span style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>{claim}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Source messages */}
                {detail.sources?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: 6 }}>
                      Said during this call
                    </div>
                    {detail.sources.map((s, i) => (
                      <div key={i} style={{
                        background: 'rgba(255,255,255,0.04)',
                        borderRadius: 6,
                        padding: '7px 9px',
                        marginBottom: 5,
                        borderLeft: '2px solid rgba(40,200,160,0.3)',
                      }}>
                        <div style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(40,200,160,0.7)', fontWeight: 'var(--ofself-weight-semibold)', marginBottom: 3 }}>
                          {s.speaker}
                        </div>
                        <div style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(255,255,255,0.65)', lineHeight: 1.45 }}>
                          {s.content}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {detail.sources?.length === 0 && (
                  <div style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}>
                    No source messages found for this concept.
                  </div>
                )}
              </>
            )}

            {detail?.error && (
              <div style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(239,68,68,0.7)' }}>
                Could not load concept details.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{
        padding: '8px 14px',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        gap: 12,
        flexShrink: 0,
      }}>
        {[['hot', '#f97316', 'New'], ['warm', '#06b6d4', 'Growing'], ['cold', '#8b5cf6', 'Settled']].map(([s, c, l]) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: c }} />
            <span style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(255,255,255,0.35)', }}>{l}</span>
          </div>
        ))}
        {!selected && (
          <span style={{ fontSize: 'var(--ofself-text-sm)', color: 'rgba(255,255,255,0.2)', marginLeft: 'auto', fontStyle: 'italic' }}>
            click a node
          </span>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(12px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  )
}
